import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import disableSubtitleTracks from 'components/Dashboard/ParliamentLive/disableSubtitleTracks';

// POC: vbegin comes from the src query string, or the vbegin prop as fallback.
const ParliamentLiveVideoPlayer = forwardRef(({ src, vbegin, onPlayingChange }, ref) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const vbeginRef = useRef(null);
  const [feedback, setFeedback] = useState(null); // { type: 'play' | 'pause', key } — YouTube-style flash
  const [paused, setPaused] = useState(true);

  useEffect(() => {
    if (!src || !containerRef.current) return;

    vbeginRef.current = Number(new URL(src).searchParams.get('vbegin')) || vbegin || 0;

    if (!playerRef.current) {
      // video.js owns this node, so it's created imperatively, not via React's JSX.
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered');
      containerRef.current.appendChild(videoElement);

      playerRef.current = videojs(videoElement, {
        controls: true,
        fluid: true,
        preload: 'auto',
        sources: [{ src, type: 'application/x-mpegURL' }],
      });
      disableSubtitleTracks(playerRef.current);
      const flash = (type) => {
        if (!playerRef.current?.seeking()) setFeedback({ type, key: Date.now() });
      };
      playerRef.current.on('play', () => {
        setPaused(false);
        flash('play');
      });
      playerRef.current.on('pause', () => {
        setPaused(true);
        flash('pause');
      });
    } else {
      playerRef.current.src({ src, type: 'application/x-mpegURL' });
    }
  }, [src]);

  useEffect(() => {
    onPlayingChange?.(!paused);
  }, [paused, onPlayingChange]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    pause: () => playerRef.current?.pause(),
    seekToTimestamp: (unixTimestamp) => {
      const player = playerRef.current;
      if (!player || !unixTimestamp) return;
      const seconds = Math.max(0, unixTimestamp - vbeginRef.current);
      player.currentTime(seconds);
      player.play();
      setFeedback({ type: 'play', key: Date.now() });
    },
    getDuration: () => playerRef.current?.duration() ?? 0,
  }));

  return (
    <div data-vjs-player className='parliament-live-video-player'>
      <img
        src={`${import.meta.env.VITE_APP_CDNURL}/images/logo_white.svg`}
        alt='PolicyMogul'
        className='parliament-live-video-watermark'
      />
      {feedback && (
        <div key={feedback.key} className='parliament-live-video-feedback' onAnimationEnd={() => setFeedback(null)}>
          <span className={feedback.type === 'play' ? 'clip-trimmer-play-icon' : 'clip-trimmer-pause-icon'} />
        </div>
      )}
      {paused && !feedback && (
        <div className='parliament-live-video-feedback parliament-live-video-feedback-static'>
          <span className='clip-trimmer-play-icon' />
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
});

export default ParliamentLiveVideoPlayer;
